import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack, Typography } from "@mui/material";
import { useCreateSupportRequestMutation } from "Api/Mutations/Support/useCreateSupportRequestMutation";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { SupportAttachmentInput } from "Components/Support/SupportAttachmentInput";
import type { SupportFlowResult } from "Components/Support/SupportFlowResult";
import { SUPPORT_REQUEST_FORM_ID } from "Components/Support/supportFormIds";
import type { SupportFormStatus } from "Components/Support/SupportFormStatus";
import {
  useSupportRequestCategoryCodeList,
  useSupportRequestRole,
} from "Components/Support/SupportRequestCategory";
import {
  getSupportRequestSchema,
  SUPPORT_REQUEST_DESCRIPTION_MAX_LENGTH,
  SUPPORT_REQUEST_SUBJECT_MAX_LENGTH,
  type SupportRequestFormData,
} from "Components/Support/supportRequestSchema";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { Resources, useResource } from "Translations/Resources";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

const RESOURCES = Resources.Support.Request;

const DEFAULT_VALUES: Partial<SupportRequestFormData> = {
  subject: "",
  description: "",
  attachment: null,
  name: "",
  email: "",
};

type Props = {
  /** Set when returning here from a failed send, so the retry starts from what was
      already typed instead of a blank form. */
  defaultValues?: Partial<SupportRequestFormData>;
  onStatusChange: (status: SupportFormStatus) => void;
  onSubmitted: (
    result: SupportFlowResult,
    submittedValues: SupportRequestFormData,
  ) => void;
};

export const SupportRequestFormStep = ({
  defaultValues,
  onStatusChange,
  onSubmitted,
}: Props) => {
  const { t } = useResource();
  const user = useLoggedInUser();
  const isAnonymous = !user;
  const categories = useSupportRequestCategoryCodeList();
  const supportRole = useSupportRequestRole();

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    formState: { isDirty },
  } = useForm<SupportRequestFormData>({
    resolver: zodResolver(getSupportRequestSchema(t, isAnonymous, supportRole)),
    defaultValues: defaultValues ?? DEFAULT_VALUES,
  });

  const { mutate: createSupportRequest, isPending } =
    useCreateSupportRequestMutation({
      onSuccess: () => {
        onSubmitted(
          {
            variant: "success",
            message: t(RESOURCES.SuccessMessage),
            canRetry: false,
          },
          getValues(),
        );
      },
      onError: (error) => {
        // 429 = the per-IP limit is exhausted. A generic message would tell the user to retry
        // right away, which is exactly what the limit is holding shut — so no retry button either.
        const isRateLimited = error.status === 429;

        onSubmitted(
          {
            variant: "error",
            message: t(
              isRateLimited
                ? RESOURCES.RateLimitedMessage
                : RESOURCES.ErrorMessage,
            ),
            canRetry: !isRateLimited,
          },
          getValues(),
        );
      },
    });

  // React Hook Form and the mutation expose these only as derived render state, so
  // lifting them to the dialog — which owns the footer buttons and the discard-changes
  // guard — takes an effect.
  useEffect(() => {
    onStatusChange({ isDirty, isBusy: isPending });
  }, [isDirty, isPending, onStatusChange]);

  const onSubmit = ({
    category,
    subject,
    description,
    attachment,
    name,
    email,
  }: SupportRequestFormData) => {
    createSupportRequest({
      category,
      subject,
      description,
      attachment,
      // Only an anonymous sender identifies themselves through the form. For a signed-in user the
      // server takes name, e-mail and public ID from the token — the body could carry a stranger's
      // address.
      reporterName: isAnonymous ? name : null,
      reporterEmail: isAnonymous ? email : null,
    });
  };

  const descriptionLength = watch("description")?.length ?? 0;

  return (
    <form id={SUPPORT_REQUEST_FORM_ID} onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={2}>
        {isAnonymous && (
          <>
            <FormInput
              control={control}
              name="name"
              label={t(RESOURCES.NameLabel)}
              formControlClass="fullWidth"
              fullWidth
            />
            <FormInput
              control={control}
              name="email"
              type="email"
              label={t(RESOURCES.EmailLabel)}
              formControlClass="fullWidth"
              fullWidth
            />
          </>
        )}
        <Dropdown
          control={control}
          name="category"
          codeList={categories}
          label={t(RESOURCES.CategoryLabel)}
          placeholder={t(RESOURCES.CategoryPlaceholder)}
        />
        <FormInput
          control={control}
          name="subject"
          label={t(RESOURCES.SubjectLabel)}
          maxLength={SUPPORT_REQUEST_SUBJECT_MAX_LENGTH}
          formControlClass="fullWidth"
          fullWidth
        />
        <FormInput
          control={control}
          name="description"
          label={t(RESOURCES.DescriptionLabel)}
          multiline
          rows={6}
          maxLength={SUPPORT_REQUEST_DESCRIPTION_MAX_LENGTH}
          formControlClass="fullWidth"
          fullWidth
          helperText={
            <Box display="flex" justifyContent="flex-end" component="span">
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
              >
                {t(RESOURCES.DescriptionCharCount, {
                  count: descriptionLength,
                  maxCount: SUPPORT_REQUEST_DESCRIPTION_MAX_LENGTH,
                })}
              </Typography>
            </Box>
          }
        />
        <SupportAttachmentInput
          control={control}
          name="attachment"
          label={t(RESOURCES.AttachmentLabel)}
        />
      </Stack>
    </form>
  );
};
